import type { ScriptableContext } from 'chart.js'
import { format } from 'date-fns/format'

import type { TimelineData } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/types'

import {
  audioIcon,
  boughtIcon,
  lockIcon,
  photoIcon,
  textIcon,
  timeIcon,
  videoIcon,
} from './consts'

export const getCustomTooltip =
  (data: TimelineData[]) =>
  (context: ScriptableContext<'line'> & { tooltip: any }) => {
    // Tooltip Element
    let tooltipEl = document.getElementById('chartjs-tooltip')

    const hideTooltip = () => {
      if (tooltipEl) {
        tooltipEl.style.opacity = '0'
        tooltipEl.style.zIndex = '-1'
      }
    }

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = 'chartjs-tooltip'
      document.body.appendChild(tooltipEl)

      tooltipEl.addEventListener('mouseleave', hideTooltip)
    }

    // Hide if no tooltip
    const tooltipModel = context.tooltip

    if (tooltipModel.opacity === 0 && !tooltipEl.matches(':hover')) {
      // prevent flickering on move mouse from image to tooltip
      setTimeout(() => {
        if (tooltipModel.opacity === 0 && !tooltipEl.matches(':hover')) {
          hideTooltip()
        }
      }, 200)

      return
    }

    if (tooltipEl) {
      const item = data[context.tooltip.dataPoints[0].dataIndex]

      if (!item) {
        return
      }
      const { photoCount, videoCount, audioCount, text, messageId, isOpened } =
        item
      const sendTime = format(new Date(item.createdAt), 'HH:mm:ss')
      let textContent = ''

      if (text) {
        textContent = `${text.substring(0, 150)} ${text.length > 150 ? '...' : ''}` // max 150 character

        // line break every 50 character
        textContent = textContent.replace(/(.{50})/g, '$1\n')
      }

      tooltipEl.classList.add(
        'bg-white',
        'absolute',
        'dark:bg-secondary-600',
        'p-4',
        'rounded-lg',
        'text-sm',
        'font-inter',
        'text-title',
        'w-max',
        '-z-1',
        'max-w-[400px]',
        'above', // caret position
      )
      const list = [
        photoCount > 0 && {
          icon: photoIcon,
          label: 'Photos:',
          value: photoCount,
        },
        videoCount > 0 && {
          icon: videoIcon,
          label: 'Videos:',
          value: videoCount,
        },
        audioCount > 0 && {
          icon: audioIcon,
          label: 'Audios:',
          value: audioCount,
        },
        textContent.length > 0 && {
          icon: textIcon,
          label: 'Text:',
          value: textContent,
        },
      ].filter((item): item is { icon: string; label: string; value: number } =>
        Boolean(item),
      )

      const content = list
        .map(
          (item) => `
        <div class="flex items-start text-sm">
          <div class="flex items-center">
            <span class="mr-[4px]">${item.icon}</span>
            <span class="mr-[8px]">${item.label}</span>
          </div>
          <span class="font-medium whitespace-pre-wrap">${item.value}</span>
        </div>
      `,
        )
        .join('')

      const mediaStatus = {
        icon: isOpened ? boughtIcon : lockIcon,
        title: isOpened ? 'Bought' : 'Locked',
      }

      tooltipEl.innerHTML = `
      <div class="space-y-3 !hover:opacity-100">
        <div class="font-medium text-base flex items-center">
          <span class="mr-[4px]" title="${mediaStatus.title}">${mediaStatus.icon}</span>
          Sent Media
        </div>
        ${content}
        <div class="border-b border-border-secondary"></div>
        <div class="flex items-center gap-6 justify-between text-sm">
          <div class="flex items-center">
            <span class="mr-[4px]">${timeIcon}</span>
            <span>${sendTime}</span>
          </div>
          <div data-message-id="${messageId}" class="cursor-pointer text-primary-500 font-medium message-link">View Chat</div>
        </div>
      </div>
    `
    }

    const { left, top } = context.chart.canvas.getBoundingClientRect()

    tooltipEl.style.opacity = '1'
    tooltipEl.style.zIndex = '5000'
    tooltipEl.style.left =
      left + window.pageXOffset + tooltipModel.caretX + 'px'
    tooltipEl.style.top =
      top + window.pageYOffset + tooltipModel.caretY + 42 + 'px'
  }
